/**
 * data/instrumentCatalog/funds.ts
 * Fund instrument reference catalog
 * 
 * Contains core equity fund instruments via liquid, widely-used ETFs across
 * US, international, emerging markets, and UCITS variants for global accessibility.
 * 
 * Data source: Major fund providers (Vanguard, iShares, SPDR, Schwab)
 * Research file: docs/reference/InstrumentCatalogResearch/Funds_DataPopulationResearch.md
 */

import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';

/**
 * Fund instrument catalog
 * 
 * Contains 43 comprehensive equity fund instruments across major categories:
 * - US Equity Funds (Large/mid/small cap, growth/value, dividends, sectors)
 * - Factor Funds (Min vol, momentum, quality, value, size - US & international)
 * - International Equity Funds (Developed ex-US, total international)
 * - Regional Funds (Asia ex-Japan, Japan, Latin America, Frontier markets)
 * - Global Equity Funds (All-world, global developed + EM)
 * - Emerging Market Funds (Large/mid, all-cap, thematic)
 * - UCITS Variants (European-domiciled for global accessibility)
 * 
 * All instruments are highly liquid ETFs with standardized pricing.
 */
export const FUNDS_INSTRUMENTS: InstrumentCatalogEntry[] = [
  
  // ===== US LARGE-CAP BLEND FUNDS =====
  
  {
    id: 'SPY_FUNDS',
    symbol: 'SPY',
    name: 'SPDR S&P 500 ETF Trust',
    shortName: 'SPDR S&P 500',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'large-cap-blend'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Tracks S&P 500; extremely liquid'
  },
  
  {
    id: 'IVV_FUNDS',
    symbol: 'IVV',
    name: 'iShares Core S&P 500 ETF',
    shortName: 'iShares Core S&P 500',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'large-cap-blend'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Lower cost S&P 500 exposure'
  },

  {
    id: 'VOO_FUNDS',
    symbol: 'VOO',
    name: 'Vanguard S&P 500 ETF',
    shortName: 'Vanguard S&P 500',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'total-market'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Vanguard S&P 500 exposure'
  },

  // ===== US TOTAL MARKET FUNDS =====

  {
    id: 'VTI_FUNDS',
    symbol: 'VTI',
    name: 'Vanguard Total Stock Market ETF',
    shortName: 'Vanguard Total Stock',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'total-market'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Entire US stock market'
  },

  {
    id: 'ITOT_FUNDS',
    symbol: 'ITOT',
    name: 'iShares Core S&P Total U.S. Stock Market ETF',
    shortName: 'iShares Core Total US',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'total-market'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Broad US market exposure'
  },

  // ===== US GROWTH FUNDS =====

  {
    id: 'VUG_FUNDS',
    symbol: 'VUG',
    name: 'Vanguard Growth ETF',
    shortName: 'Vanguard Growth',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'large-cap-growth'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Large-cap growth focus'
  },

  {
    id: 'IWF_FUNDS',
    symbol: 'IWF',
    name: 'iShares Russell 1000 Growth ETF',
    shortName: 'iShares Russell 1000 Growth',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'large-cap-growth'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Russell 1000 Growth'
  },

  // ===== US VALUE FUNDS =====

  {
    id: 'VTV_FUNDS',
    symbol: 'VTV',
    name: 'Vanguard Value ETF',
    shortName: 'Vanguard Value',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'large-cap-value'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Large-cap value focus'
  },

  {
    id: 'IWD_FUNDS',
    symbol: 'IWD',
    name: 'iShares Russell 1000 Value ETF',
    shortName: 'iShares Russell 1000 Value',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'large-cap-value'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Russell 1000 Value'
  },

  // ===== US MID-CAP FUNDS =====

  {
    id: 'VO_FUNDS',
    symbol: 'VO',
    name: 'Vanguard Mid-Cap ETF',
    shortName: 'Vanguard Mid-Cap',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'mid-cap-blend'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Mid-cap broad exposure'
  },

  {
    id: 'IJH_FUNDS',
    symbol: 'IJH',
    name: 'iShares Core S&P Mid-Cap ETF',
    shortName: 'iShares Core Mid-Cap',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'mid-cap-blend'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'S&P Mid-Cap 400'
  },

  // ===== US SMALL-CAP FUNDS =====

  {
    id: 'VB_FUNDS',
    symbol: 'VB',
    name: 'Vanguard Small-Cap ETF',
    shortName: 'Vanguard Small-Cap',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'small-cap-blend'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Small-cap broad exposure'
  },

  {
    id: 'IJR_FUNDS',
    symbol: 'IJR',
    name: 'iShares Core S&P Small-Cap ETF',
    shortName: 'iShares Core Small-Cap',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'small-cap-blend'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'S&P Small-Cap 600'
  },

  // ===== US DIVIDEND FUNDS =====

  {
    id: 'VYM_FUNDS',
    symbol: 'VYM',
    name: 'Vanguard High Dividend Yield ETF',
    shortName: 'Vanguard High Dividend',
    classification: {
      assetClass: 'funds',
      marketSegment: 'regional',
      subAssetClass: 'europe'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'High dividend yield focus'
  },

  {
    id: 'HDV_FUNDS',
    symbol: 'HDV',
    name: 'iShares Core High Dividend ETF',
    shortName: 'iShares Core High Dividend',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'dividend'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Quality high dividend stocks'
  },

  // ===== INTERNATIONAL DEVELOPED FUNDS =====

  {
    id: 'VEA_FUNDS',
    symbol: 'VEA',
    name: 'Vanguard FTSE Developed Markets ETF',
    shortName: 'Vanguard Developed Mkts',
    classification: {
      assetClass: 'funds',
      marketSegment: 'international',
      subAssetClass: 'developed-markets'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Developed markets ex-US'
  },

  {
    id: 'EFA_FUNDS',
    symbol: 'EFA',
    name: 'iShares MSCI EAFE ETF',
    shortName: 'iShares MSCI EAFE',
    classification: {
      assetClass: 'funds',
      marketSegment: 'international',
      subAssetClass: 'developed-markets'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Europe, Australasia, Far East'
  },

  // ===== EMERGING MARKETS FUNDS =====

  {
    id: 'VWO_FUNDS',
    symbol: 'VWO',
    name: 'Vanguard FTSE Emerging Markets ETF',
    shortName: 'Vanguard Emerging Mkts',
    classification: {
      assetClass: 'funds',
      marketSegment: 'emerging-markets',
      subAssetClass: 'broad-market'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Broad emerging markets'
  },

  {
    id: 'EEM_FUNDS',
    symbol: 'EEM',
    name: 'iShares MSCI Emerging Markets ETF',
    shortName: 'iShares MSCI EM',
    classification: {
      assetClass: 'funds',
      marketSegment: 'emerging-markets',
      subAssetClass: 'broad-market'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'MSCI emerging markets'
  },

  // ===== GLOBAL FUNDS =====

  {
    id: 'VT_FUNDS',
    symbol: 'VT',
    name: 'Vanguard Total World Stock ETF',
    shortName: 'Vanguard Total World',
    classification: {
      assetClass: 'funds',
      marketSegment: 'global',
      subAssetClass: 'all-world'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Global stock market'
  },

  {
    id: 'ACWI_FUNDS',
    symbol: 'ACWI',
    name: 'iShares MSCI ACWI ETF',
    shortName: 'iShares MSCI ACWI',
    classification: {
      assetClass: 'funds',
      marketSegment: 'global',
      subAssetClass: 'all-world'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'All Country World Index'
  },

  // ===== FACTOR FUNDS =====

  {
    id: 'USMV_FUNDS',
    symbol: 'USMV',
    name: 'iShares MSCI USA Min Vol Factor ETF',
    shortName: 'iShares Min Vol',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'factor'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Minimum volatility factor'
  },

  {
    id: 'QUAL_FUNDS',
    symbol: 'QUAL',
    name: 'iShares MSCI USA Quality Factor ETF',
    shortName: 'iShares Quality',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'factor'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Quality factor exposure'
  },

  {
    id: 'MTUM_FUNDS',
    symbol: 'MTUM',
    name: 'iShares MSCI USA Momentum Factor ETF',
    shortName: 'iShares Momentum',
    classification: {
      assetClass: 'funds',
      marketSegment: 'us-equities',
      subAssetClass: 'factor'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Momentum factor exposure'
  },

  // ===== REGIONAL FUNDS =====

  {
    id: 'FXI_FUNDS',
    symbol: 'FXI',
    name: 'iShares China Large-Cap ETF',
    shortName: 'iShares China Large-Cap',
    classification: {
      assetClass: 'funds',
      marketSegment: 'regional',
      subAssetClass: 'china'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'China large-cap exposure'
  },

  {
    id: 'EWJ_FUNDS',
    symbol: 'EWJ',
    name: 'iShares MSCI Japan ETF',
    shortName: 'iShares Japan',
    classification: {
      assetClass: 'funds',
      marketSegment: 'regional',
      subAssetClass: 'japan'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Japan equity exposure'
  },

  {
    id: 'VGK_FUNDS',
    symbol: 'VGK',
    name: 'Vanguard FTSE Europe ETF',
    shortName: 'Vanguard Europe',
    classification: {
      assetClass: 'funds',
      marketSegment: 'regional',
      subAssetClass: 'europe'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'European equity exposure'
  }
];